import {
  AlertLooks,
  LabelPosition,
  MoreInfoTooltip,
  StandardAlert,
  StandardButton,
  StandardSwitch
} from '@brandfolder/react';
import classnames from 'classnames';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { Form, Formik, FormikErrors, FormikTouched } from 'formik';
import { array, boolean, mixed, object, SchemaOf, string } from 'yup';

import { useFetch } from '@api/ApiHelper';
import { ServerUserRoles } from '@api/v4/private/resources/users';
import { moreInfoLabel } from '@components/standard-messages';
import { ListDropdown, ListOptionValueNoNull, MultiselectDropdown } from '@components/library/dropdown';
import { StandardTextField } from '@components/library/text-field';
import { Brandfolder, Portal, ServerResourceType } from '@components/bulk_management/user-settings/resource-sidebar';
import {
  getBrandfolderCollectionKeys,
  getCollectionChildKeys,
  resourceDropdownHelperText,
  REUSABLE_EMAIL,
  userPermissionOptions,
  userPermissionPluralOptions
} from '@components/bulk_management/user-settings/helpers';
import {
  currentTabErrorsIsInvite,
  currentTabErrorsIsMessage,
  currentTabTouchedIsInvite,
  currentTabTouchedIsMessage,
  currentTabValuesIsInvite,
  currentTabValuesIsMessage,
  FormTabs,
  InvitationFormValues,
  MessageFormValues
} from '@components/bulk_management/user-settings/invitations/form-type-helpers';
import {
  generateBrandfolderCollectionOptions,
  PermissionLevelTooltip
} from '@components/bulk_management/user-settings/invitations/form-helper-components';
import { EmailAddresses } from './EmailAddresses';
import { emailAddressRegex } from '@helpers/emails';

import '../styles/invitation-form.scss';

enum AlertStates {
  InvitationError,
  InvitationSuccess,
  MessageError,
  MessageSuccess,
  None
}

interface InvitationFormProps {
  brandfolders: Brandfolder[];
  currentTab: FormTabs;
  portals: Portal[];
  selectedResourceKey: string;
  selectedResourceType: 'organization' | 'brandfolder' | 'collection' | 'portal' | 'brandguide';
  setReloadPendingInvitations: SetStateDispatch<boolean>;
  setReloadPlanLimits: SetStateDispatch<boolean>;
  setReloadUsers: SetStateDispatch<boolean>;
}

const invitationValidationSchema: SchemaOf<InvitationFormValues> = object().shape({
  resources: array()
    .of(object().shape({
      type: mixed<ServerResourceType>(),
      key: string()
    })).min(1, bfTranslate('Resource is required.')),
  emails: array()
    .of(string()
      .test('emailAddresses', bfTranslate('Invalid email address.'), (value): boolean => {
        if (value === REUSABLE_EMAIL) return true;
        return emailAddressRegex.test(value);
      }))
    .min(1, bfTranslate('At least one email address is required.')),
  permissionLevel: mixed<ServerUserRoles>()
    .oneOf([ServerUserRoles.Admin, ServerUserRoles.Collaborator, ServerUserRoles.Guest]),
  personalMessage: string(),
  preventEmail: boolean()
});

/**
 * this variable is coerced instead of explicitly typed
 * because enums and enum arrays are not well supported
 * in the yup types
 * see https://github.com/jquense/yup/issues/1143
 * https://github.com/jquense/yup/issues/1230
 * https://github.com/jquense/yup/issues/1013
 */
const messageValidationSchema = object().shape({
  resources: array()
    .of(object().shape({
      type: mixed<ServerResourceType>(),
      key: string()
    })).min(1, bfTranslate('Resource is required.')),
  recipients: array(mixed<ServerUserRoles>()
    .oneOf([ServerUserRoles.Admin, ServerUserRoles.Collaborator, ServerUserRoles.Guest]))
    .min(1, bfTranslate('Recipient is required.')),
  personalMessage: string()
    .required(bfTranslate('A message is required.'))
}) as SchemaOf<MessageFormValues>;

const getFirstError = (errs: string[] | FormikErrors<any>[]): string | FormikErrors<any> | undefined => {
  const errorIndex = errs.findIndex((err) => err !== undefined);
  if (errorIndex === -1) {
    return undefined;
  }

  return errs[errorIndex];
};

export const InvitationForm: FunctionComponent<InvitationFormProps> = ({
  brandfolders,
  currentTab,
  portals,
  selectedResourceKey,
  selectedResourceType,
  setReloadPendingInvitations,
  setReloadPlanLimits,
  setReloadUsers
}) => {
  const [clearTags, setClearTags] = useState(false);
  const [submittedMessageValues, setSubmittedMessageValues] = useState<MessageFormValues | undefined>();
  const [submittedInvitationValues, setSubmittedInvitationValues] = useState<InvitationFormValues | undefined>();
  // fake loading because it displays while we wait to trigger reload of other components
  // rather than the actual loading of waiting for fetch to return
  const [fakeLoading, setFakeLoading] = useState(false);
  const [showAlert, setShowAlert] = useState<AlertStates>(AlertStates.None);

  const bulkCreateInvitationFetch = useFetch({
    url: '/api/v4/invitations',
    fetchOnMount: false,
    method: 'POST',
    /* eslint-disable @typescript-eslint/naming-convention */
    body: {
      data: {
        resources: submittedInvitationValues?.resources.map((resource) => ({
          resource_type: resource.type,
          resource_key: resource.key
        })),
        attributes: {
          emails: [...new Set(submittedInvitationValues?.emails)],
          permission_level: submittedInvitationValues?.permissionLevel,
          personal_message: submittedInvitationValues?.personalMessage || undefined,
          prevent_email: submittedInvitationValues?.preventEmail
        }
      }
    }
    /* eslint-enable @typescript-eslint/naming-convention */
  });

  const messageFetch = useFetch({
    url: '/api/v4/notifications',
    fetchOnMount: false,
    method: 'POST',
    /* eslint-disable @typescript-eslint/naming-convention */
    body: {
      data: {
        attributes: {
          notification_type: 'message',
          context: {
            resources: submittedMessageValues?.resources.map((resource) => ({
              resource_type: resource.type,
              resource_key: resource.key
            })),
            message: submittedMessageValues?.personalMessage,
            recipients: submittedMessageValues?.recipients,
            sender_key: BFG.currentUser?.user_id
          }
        }
      }
    }
    /* eslint-enable @typescript-eslint/naming-convention */
  });

  useEffect(() => {
    if (submittedInvitationValues) {
      bulkCreateInvitationFetch.fetch();
      setFakeLoading(true);
    }
  }, [submittedInvitationValues]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (submittedMessageValues) {
      messageFetch.fetch();
    }
  }, [submittedMessageValues]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (bulkCreateInvitationFetch.response) {
      // give the bulk invitation job 1 second to reasonably complete
      setTimeout(() => {
        setReloadPendingInvitations(true);
        setReloadPlanLimits(true);
        setReloadUsers(true);
        setShowAlert(AlertStates.InvitationSuccess);
        setFakeLoading(false);
        setClearTags(true);
      }, 1000);
    }
  }, [bulkCreateInvitationFetch.response]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (bulkCreateInvitationFetch.error) {
      setFakeLoading(false);
      setShowAlert(AlertStates.InvitationError);
    }
  }, [bulkCreateInvitationFetch.error]);

  useEffect(() => {
    if (messageFetch.response) {
      setShowAlert(AlertStates.MessageSuccess);
    }
  }, [messageFetch.response]);

  useEffect(() => {
    if (messageFetch.error) {
      setShowAlert(AlertStates.MessageError);
    }
  }, [messageFetch.error]);

  useEffect(() => {
    if (clearTags) {
      setClearTags(false);
    }
  }, [clearTags]);

  const invitationAlerts = (
    <>
      {showAlert === AlertStates.InvitationSuccess && (
        <StandardAlert
          className="invitation-form__submission-alert"
          look={AlertLooks.Success}
        >
          {bfTranslate("We're working on creating invitations. You'll receive a notification when the invitations have been sent.")}
        </StandardAlert>
      )}
      {showAlert === AlertStates.InvitationError && (
        <StandardAlert
          className="invitation-form__submission-alert"
          look={AlertLooks.Error}
        >
          {bfTranslate('Something went wrong while creating invitations. Try again or contact customer support.')}
        </StandardAlert>
      )}
    </>
  );

  const messageAlerts = (
    <>
      {showAlert === AlertStates.MessageSuccess && (
        <StandardAlert
          className="invitation-form__submission-alert"
          look={AlertLooks.Success}
        >
          {bfTranslate('Your message will be sent as a notification to all selected users.')}
        </StandardAlert>
      )}
      {showAlert === AlertStates.MessageError && (
        <StandardAlert
          className="invitation-form__submission-alert"
          look={AlertLooks.Error}
        >
          {bfTranslate('Something went wrong while submitting your message. Try again or contact customer support.')}
        </StandardAlert>
      )}
    </>
  );

  const invitationInitialValues: InvitationFormValues = {
    resources: [{
      type: selectedResourceType as ServerResourceType,
      key: selectedResourceKey
    }],
    emails: [],
    permissionLevel: ServerUserRoles.Guest,
    personalMessage: '',
    preventEmail: false,
  };

  const messageInitialValues: MessageFormValues = {
    resources: [{
      type: selectedResourceType as ServerResourceType,
      key: selectedResourceKey
    }],
    recipients: [],
    personalMessage: ''
  };

  const tabContent = (
    <Formik
      enableReinitialize
      initialValues={currentTab === 'invite' ? invitationInitialValues : messageInitialValues}
      onReset={(): void => {
        setClearTags(true);
      }}
      onSubmit={(values, { resetForm }): void => {
        if (currentTabValuesIsInvite(currentTab, values)) {
          setSubmittedInvitationValues(values);
        } else {
          setSubmittedMessageValues(values);
        }
        resetForm();
      }}
      validationSchema={currentTab === 'invite' ? invitationValidationSchema : messageValidationSchema}
    >
      {(formikBag): JSX.Element => {
        const {
          errors,
          setFieldValue,
          setFieldTouched,
          submitCount,
          touched,
          values
        } = formikBag;

        const resourceOptions = generateBrandfolderCollectionOptions(brandfolders, portals, values.resources);

        const handleMultiselectChange = (
          selectedOptions: ListOptionValueNoNull[],
          isAdded: boolean,
          updatedValue: ListOptionValueNoNull
        ): void => {
          let updatedResources = [];
          selectedOptions.forEach((option) => {
            const [type, key] = (option as string).split('|');
            updatedResources.push({ type, key });
          });

          const [type, key] = (updatedValue as string).split('|');
          if (isAdded && type === 'brandfolder') {
            const childResourceKeys = getBrandfolderCollectionKeys(key, brandfolders);
            updatedResources = updatedResources.filter((resource) => (
              !childResourceKeys.includes(resource.key)
            ));
          } else if (isAdded && type === 'collection') {
            const childResourceKeys = getCollectionChildKeys(key, brandfolders);
            updatedResources = updatedResources.filter((resource) => (
              !childResourceKeys.includes(resource.key)
            ));
          }
          setFieldValue('resources', [...new Set(updatedResources)]);
        };

        return (
          <Form id="invite-users-form">
            {currentTabValuesIsInvite(currentTab, values)
            && currentTabTouchedIsInvite(currentTab, touched)
            && currentTabErrorsIsInvite(currentTab, errors)
            && (
              <EmailAddresses
                clearTags={clearTags}
                error={(touched.emails && submitCount > 0 && (Array.isArray(errors.emails) ? getFirstError(errors.emails) as string : errors.emails))}
                setFieldTouched={setFieldTouched}
                setFieldValue={setFieldValue}
                touched={touched.emails}
              />
            )}
            <div className="invitation-form--dropdown-row">
              <MultiselectDropdown
                className={classnames(
                  'invitation-form__resource-multiselect',
                  { 'resources-error': touched.resources && errors.resources },
                  { 'has-recipients-error': (touched as FormikTouched<MessageFormValues>).recipients
                    && (errors as FormikErrors<MessageFormValues>).recipients },
                  { 'message-tab': currentTab === 'message' }
                )}
                error={touched.resources && errors.resources}
                id="resource-multiselect"
                label={
                  <div className="invitation-form__resource-multiselect__label">
                    {bfTranslate('Resource')}
                    <MoreInfoTooltip
                      iconLabel={moreInfoLabel}
                      id="invitation-form__resource-multiselect__more-info"
                      tooltip={resourceDropdownHelperText}
                    />
                  </div>
                }
                onChange={handleMultiselectChange}
                options={resourceOptions}
                searchable
                values={values.resources.map(({ key, type }) => `${type}|${key}`)}
                virtualizeOptions={resourceOptions.length > 10}
              />
              {currentTabValuesIsInvite(currentTab, values) && (
                <ListDropdown
                  className="invitation-form__permission-level"
                  label={
                    <div className="invitation-form__resource-multiselect__label">
                      {bfTranslate('Permission level')}
                      <PermissionLevelTooltip />
                    </div>
                  }
                  onChange={(selectedOption): void => {
                    setFieldValue('permissionLevel', selectedOption.value);
                  }}
                  options={userPermissionOptions}
                  value={values.permissionLevel}
                  virtualizeOptions={false}
                />
              )}
              {currentTabValuesIsMessage(currentTab, values)
              && currentTabErrorsIsMessage(currentTab, errors)
              && currentTabTouchedIsMessage(currentTab, touched)
              && (
                <MultiselectDropdown
                  className={classnames(
                    'invitation-form__permission-level',
                    { 'has-resources-error': touched.resources && errors.resources },
                    { 'recipients-error': touched.recipients && errors.recipients },
                    { 'message-tab': currentTab === 'message' }
                  )}
                  error={touched.recipients && errors.recipients}
                  id="recipient-multiselect"
                  label={bfTranslate('Recipients')}
                  onChange={(selectedOptions): void => {
                    setFieldValue('recipients', [...selectedOptions]);
                  }}
                  options={userPermissionPluralOptions}
                  values={values.recipients}
                  virtualizeOptions={false}
                />
              )}
            </div>
            <div className="invitation-form--message-row">
              <StandardTextField
                error={touched.personalMessage && errors.personalMessage}
                id="invite-users-form__personal-message"
                label={
                  <div>
                    {currentTab === 'invite' ? bfTranslate('Personal message') : bfTranslate('Message')}
                    {currentTab === 'invite' && (
                      <span className="invitation-form__label--light">
                        &nbsp;{`(${bfTranslate('optional')})`}
                      </span>
                    )}
                  </div>
                }
                multiLine
                name="invite-users-form__personal-message"
                onChange={(e): void => {
                  setFieldValue('personalMessage', e.target.value);
                }}
                placeholder=""
                value={values.personalMessage}
              />
            </div>
            <div className={
              classnames(
                'invitation-form--submission-row', {
                  'invitation-form--submission-row--right-aligned': currentTab === 'message'
                })
            }>
              {currentTabValuesIsInvite(currentTab, values) && (
                <StandardSwitch
                  className="invitation-form__prevent-emails"
                  isChecked={!values.preventEmail}
                  labelPosition={LabelPosition.Right}
                  onChange={(): void => setFieldValue('preventEmail', !values.preventEmail)}
                >
                  {bfTranslate('Send invitations via email')}
                </StandardSwitch>
              )}
              <StandardButton
                loading={fakeLoading}
                type='submit'
              >
                {currentTab === 'invite' ? bfTranslate('Send invitations') : bfTranslate('Send message')}
              </StandardButton>
            </div>
          </Form>
        );
      }}
    </Formik>
  );

  return (
    <div className="invitation-form">
      {currentTab === 'message' && (
        <>
          <h3 className="invitation-form__heading">{bfTranslate('Message Users')}</h3>
          {messageAlerts}
        </>
      )}
      {currentTab === 'invite' && (
        <>
          <h3 className="invitation-form__heading">{bfTranslate('Invite Users')}</h3>
          {invitationAlerts}
        </>
      )}
      {tabContent}
    </div>
  );
};
